import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import classNames from 'classnames';

import { getErrorMessage } from '~libs/axiosWrapper';
import { convertUrlToFile } from '~libs/dataUtils';
import { useToast } from '~libs/toast';

import { AnalyseInvoiceResponse } from '~support-at-home/models';

import { useGetCompanySettingsQuery } from '~main/actions/companySettings';
import {
  getFile,
  getOne,
  uploadInvoiceFile,
  useAnalyseInvoiceFileMutation,
} from '~support-at-home/actions/supplierInvoices';

import { clearCurrentInvoice } from '~support-at-home/reducers/supplierInvoices';

import {
  selectAllFiles,
  selectCurrentFile,
  selectCurrentSupplierInvoice,
  selectCurrentSupplierLoading,
} from '~support-at-home/selectors/supplierInvoices';

import FileOffCanvas from '../../common/FileOffCanvas';
import SupplierInvoiceModal from './SupplierInvoiceModal';

interface ComponentProps {
  id?: string;
  setId: (val: any) => void;
  load: () => void;
}

const EditSupplierInvoice: React.FC<ComponentProps> = ({ id, setId, load }) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const supplierInvoice = useAppSelector(selectCurrentSupplierInvoice);
  const supplierInvoiceLoading = useAppSelector(selectCurrentSupplierLoading);
  const file = useAppSelector(selectCurrentFile);
  const files = useAppSelector(selectAllFiles) || [];
  const [isFileCanvasCollapsed, setIsFileCanvasCollapsed] = useState<boolean>(false);
  const [ocrResult, setOcrResult] = useState<AnalyseInvoiceResponse | null>(null);
  const isLoaded = supplierInvoiceLoading === 'fulfilled';

  const { data: companySettings = [] } = useGetCompanySettingsQuery();
  const ocrSetting = companySettings.find((setting) => setting.setting.key === 'ocr_invoice_enabled');
  const ocrEnabled = ocrSetting?.value === 'true';

  const [analyseFile, { isLoading: isAnalysing }] = useAnalyseInvoiceFileMutation();

  const onClose = async (confirm: boolean, id?: string) => {
    setId(null);

    if (confirm && file && id) {
      if (file.data && file.file_name) {
        const blob = await convertUrlToFile(file.data, file.file_name);
        if (blob) {
          const newFile = new File([blob], file.file_name, { type: file.file_type });
          dispatch(uploadInvoiceFile({ file: newFile, id }));
        }
      }
    }

    setOcrResult(null);
    dispatch(clearCurrentInvoice({}));
    load();
  };

  useEffect(() => {
    if (id) {
      dispatch(getOne({ id }));
      setOcrResult(null);
    }
  }, [id]);

  const invoiceFileId = supplierInvoice?.file?.id;

  useEffect(() => {
    if (!id || !isLoaded || !invoiceFileId) return;
    dispatch(getFile({ id: invoiceFileId }));
  }, [id, isLoaded, invoiceFileId]);

  const handleUpload = async (file: any) => {
    // Dispatch the upload action with the new file
    const { payload } = await dispatch(uploadInvoiceFile({ file, id: id ?? '' }));
    if (payload?.success) {
      dispatch(getOne({ id }));
    }
  };

  const handleFileDeleted = () => {
    // Refresh invoice data after file deletion
    if (id) {
      dispatch(getOne({ id }));
    }
  };

  const handleAnalyse = async (fileId: string) => {
    try {
      const result = await analyseFile({ fileId }).unwrap();
      setOcrResult(result);
      showToast('success', 'Invoice analysed — please review the extracted fields.');
    } catch (err) {
      console.error('Invoice analysis failed:', err);
      showToast('danger', getErrorMessage(err));
    }
  };

  const modalClassName = classNames({
    'full-view': isFileCanvasCollapsed,
  });

  return (
    <>
      <SupplierInvoiceModal
        className={modalClassName}
        isOpen={!!id}
        onClose={onClose}
        isNew={false}
        ocrResult={ocrResult}
      />
      <FileOffCanvas
        isOpen={!!id}
        onChange={(file) => {
          handleUpload(file);
        }}
        onToggleCollapse={setIsFileCanvasCollapsed}
        isExisting={true}
        selectorFiles={files}
        onFileDeleted={handleFileDeleted}
        onAnalyse={handleAnalyse}
        isAnalysing={isAnalysing}
        analyseEnabled={ocrEnabled}
      />
    </>
  );
};

export default EditSupplierInvoice;
